// env-file.ts — session-PAT .env building + download helpers.
//
// Shared between:
//   - `apps/web/src/pages/ExternalClientsNest.tsx` (approve-and-mint reveal panel,
//     CTSK-176 origin) — the "full" .env with apiVersionAtMint + subUuidTail.
//   - `apps/web/src/components/PatsPanel.tsx` (Settings PAT mint reveal,
//     Settings-mint parity ship 2026-07-28) — the "settings" .env with the
//     four minimum fields.
//
// Two build functions instead of one because the two mint paths return
// different payload shapes:
//
//   - Approve-and-mint (Nest flow) returns `ApproveResult['mintedPat']` which
//     includes `apiVersionAtMint`, `apiBaseUrl`, `subUuidTailFirst8` (the
//     server-composed diagnostic surface from CTSK-190 + CTSK-187 + CTSK-191).
//     → `buildSessionEnv(pat, baseUrl, sourceTag)`
//
//   - Direct Settings mint (`POST /pats`) returns `PersonalAccessTokenWithSecret`
//     which carries only the base fields (id, name, tier, sourceTag, token).
//     The diagnostic-only extras aren't in the response — the daemon works
//     without them.
//     → `buildSettingsSessionEnv({name, token, id, sourceTag}, baseUrl)`
//
// `envFilenameFor` + `triggerDownload` + `pickApiBaseUrl` are byte-identical
// across the two callers and live here as the single source of truth.
//
// Origin trail:
//   - CTSK-176 shipped the approve-and-mint .env download (Nest side)
//   - CTSK-187 shipped the apiBaseUrl fallback chain (`pickApiBaseUrl`)
//   - 2026-07-28 Settings-mint .env parity ship duplicated 3 of these helpers
//     into PatsPanel with a self-noted "extract to shared lib" follow-up
//   - This file is that follow-up (2026-07-29).

/**
 * Filesystem-safe filename convention used at ~/.config/tasko-mcp/.
 * Rule: lowercase + hyphens-to-underscores + `.env` suffix.
 * See memory `reference-tasko-session-env-naming-convention`.
 */
export function envFilenameFor(sourceTag: string): string {
  return sourceTag.toLowerCase().replace(/-/g, '_') + '.env';
}

// ── CTSK-311 (0.1.125) — role-aware .env emission ───────────────────

/**
 * Role-scoped default container names stamped into the downloaded .env's
 * TASKO_CONTAINER when the role emits extended keys. `clipper-inbox` is
 * the reasonable default for the tootasko-clipper extension (operator can
 * override in extension config); nest-scoped defaults arrive with the
 * CTSK-312 integration-templates EPIC.
 */
export const ROLE_ENV_DEFAULT_CONTAINERS: Record<string, string> = {
  'tootasko-ext': 'clipper-inbox',
};

/**
 * Role-specific context for the extended .env emission. All fields
 * optional — an absent/unknown role emits nothing extra, preserving the
 * pre-CTSK-311 file byte-for-byte (regression guarantee, AC 9/12).
 */
export interface RoleEnvContext {
  /** ROLE_CATALOG handle from the mint response (`mintedPat.roleHandle`). */
  roleHandle?: string | null;
  /** Mint org from the mint response (`mintedPat.activeOrgId`). */
  activeOrgId?: string | null;
  /** Web-UI origin for TASKO_WEB_UI_URL. Callers pass
   *  `window.location.origin` — same as TASKO_BASE_URL on Preview/Prod
   *  (shared origin); the operator's UI origin (`:5173`) on dev. Kept a
   *  parameter (not read from `window` here) so the builder stays pure
   *  and unit-testable. */
  webUiUrl?: string | null;
}

/**
 * Extra `TASKO_*` lines emitted for tool-integration roles (CTSK-311).
 * Today only `tootasko-ext` opts in — the four keys cover the
 * tootasko-clipper extension's full Captain-Tasko config surface so the
 * mint → download → Import-.env round-trip is turnkey (amendment
 * 01KZC2PC6SEP5DBJ44N00Y0BCM, ACs 9-13). Every other role returns []
 * (unchanged emission — backwards compatible).
 */
export function buildRoleEnvLines(role: RoleEnvContext | undefined): string[] {
  if (!role || role.roleHandle !== 'tootasko-ext') return [];
  return [
    `TASKO_WEB_UI_URL="${role.webUiUrl ?? ''}"`,
    `TASKO_ORG_ID="${role.activeOrgId ?? ''}"`,
    `TASKO_CONTAINER="${ROLE_ENV_DEFAULT_CONTAINERS[role.roleHandle] ?? ''}"`,
    `TASKO_DSR_DIRECT_POST="true"`,
  ];
}

/**
 * Full-form .env for the approve-and-mint Nest flow. Includes the diagnostic
 * fields (`apiVersionAtMint`, `subUuidTailFirst8`) captured by the server-side
 * approve+mint path. See CTSK-173 (Path F design) for the consumer contract.
 *
 * CTSK-311: pass `role` (handle + org + UI origin from the mint reveal) to
 * emit the extended tool-integration keys for roles that declare them
 * (currently `tootasko-ext` only). Omitted/unknown role → output identical
 * to the pre-0.1.125 file.
 */
export function buildSessionEnv(
  pat: {
    token: string;
    id: string;
    name: string;
    apiVersionAtMint?: string | null;
    subUuidTailFirst8?: string | null;
  },
  baseUrl: string,
  sourceTag: string,
  role?: RoleEnvContext,
): string {
  const nowIso = new Date().toISOString();
  const subUuidTail = pat.subUuidTailFirst8 ?? '';
  const apiVersionAtMint = pat.apiVersionAtMint ?? 'unknown';
  // CTSK-394: omit the line entirely when the access-request carried no
  // sessionMetadata.sessionKey (webhook / no-session clients) — a bare
  // `TASKO_SUB_UUID_TAIL=""` was a cosmetic gap the tail auto-stamping
  // (CTSK-176/207/211) never closed.
  const subUuidTailLine = subUuidTail ? [`TASKO_SUB_UUID_TAIL="${subUuidTail}"`] : [];
  return [
    `# Captain Tasko session PAT — auto-generated ${nowIso}`,
    `# For: ${pat.name}`,
    `# Save to: ~/.config/tasko-mcp/${envFilenameFor(sourceTag)}`,
    `# Then: chmod 600 ~/.config/tasko-mcp/${envFilenameFor(sourceTag)}`,
    `# Do NOT commit. Do NOT share.`,
    `#`,
    `# captain-tasko API version at mint: ${apiVersionAtMint}`,
    ``,
    `TASKO_BASE_URL="${baseUrl}"`,
    `TASKO_PAT="${pat.token}"`,
    `TASKO_SOURCE="${sourceTag}"`,
    `TASKO_PAT_ID="${pat.id}"`,
    ...subUuidTailLine,
    `TASKO_API_VERSION_AT_MINT="${apiVersionAtMint}"`,
    ...buildRoleEnvLines(role),
    ``,
  ].join('\n');
}

/**
 * Minimal-form .env for the direct Settings mint flow (POST /pats). Omits
 * the approve-and-mint-only diagnostic fields (apiVersionAtMint,
 * subUuidTailFirst8) that aren't returned by /pats POST. The daemon works
 * without them.
 */
export function buildSettingsSessionEnv(
  pat: { name: string; token: string; id: string; sourceTag: string },
  baseUrl: string,
): string {
  const nowIso = new Date().toISOString();
  return [
    `# Captain Tasko session PAT — auto-generated ${nowIso}`,
    `# For: ${pat.name}`,
    `# Save to: ~/.config/tasko-mcp/${envFilenameFor(pat.sourceTag)}`,
    `# Then: chmod 600 ~/.config/tasko-mcp/${envFilenameFor(pat.sourceTag)}`,
    `# Do NOT commit. Do NOT share.`,
    `#`,
    `# Minted via Settings → PATs → New token (Settings-path mint —`,
    `# the approve-and-mint flow in the External Clients Nest emits`,
    `# additional diagnostic fields like TASKO_API_VERSION_AT_MINT +`,
    `# TASKO_SUB_UUID_TAIL which aren't returned by /pats POST).`,
    ``,
    `TASKO_BASE_URL="${baseUrl}"`,
    `TASKO_PAT="${pat.token}"`,
    `TASKO_SOURCE="${pat.sourceTag}"`,
    `TASKO_PAT_ID="${pat.id}"`,
    ``,
  ].join('\n');
}

/**
 * Trigger a browser download of a text blob with the given filename.
 * Uses the standard anchor-tag + URL.createObjectURL pattern; works on
 * every browser + all http/https origins (unlike navigator.clipboard,
 * which needs a secure context).
 */
export function triggerDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── CTSK-187 — API base URL fallback chain ──────────────────────────

/** Hostnames that are only reachable from the API's own machine. */
const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1', '0.0.0.0']);

function isLoopbackHostname(hostname: string): boolean {
  return LOOPBACK_HOSTNAMES.has(hostname.toLowerCase());
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * CTSK-211: pick the API base URL stamped into the downloaded .env's
 * TASKO_BASE_URL. Ratified chain (comment 01KYE76CR8W7KW2GGZXR2K5S9G,
 * dr.the0 review 2026-07-26) — first hit wins:
 *
 *   1. `VITE_API_PUBLIC_BASE_URL` — build-time env. Explicit config
 *      outranks everything (prod-friendly).
 *   2. `serverValue` — the CTSK-187 mint-response `apiBaseUrl`, GATED:
 *      discarded when its hostname is loopback (`localhost`,
 *      `127.0.0.1`, `[::1]`, `0.0.0.0`) while
 *      `window.location.hostname` is NOT.
 *   3. Client derivation — `window.location.origin` with the current
 *      port swapped to `VITE_API_PUBLIC_PORT` (default 4000).
 */
export function pickApiBaseUrl(serverValue?: string | null): string {
  // 1. Build-time env — explicit config wins.
  const buildTime = import.meta.env.VITE_API_PUBLIC_BASE_URL;
  if (buildTime) return stripTrailingSlash(buildTime);

  // 2. Server-derived value (CTSK-187), gated against the loopback trap.
  if (serverValue) {
    try {
      const serverIsLoopback = isLoopbackHostname(new URL(serverValue).hostname);
      const pageIsLoopback = isLoopbackHostname(window.location.hostname);
      if (!serverIsLoopback || pageIsLoopback) {
        return stripTrailingSlash(serverValue);
      }
    } catch {
      // Malformed server value — fall through to client derivation.
    }
  }

  // 3. Client derivation: page origin with the API's public port.
  const derived = new URL(window.location.origin);
  derived.port = String(import.meta.env.VITE_API_PUBLIC_PORT ?? '4000');
  return stripTrailingSlash(derived.origin);
}
